import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  List,
  ListItem,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { COMPONENTS_MAP } from '../../components-map';

/**
 * @fileoverview Componente selector de gráficos y widgets.
 * @module ChartSelector
 */

/**
 * Componente ChartSelector.
 *
 * Proporciona una interfaz para que el usuario navegue y seleccione entre los
 * distintos tipos de widgets disponibles en la aplicación.
 *
 * Flujo de uso:
 * 1. Muestra un cajón lateral (Drawer) con la lista de componentes disponibles.
 * 2. Al seleccionar uno, abre un diálogo modal para personalizar el título inicial.
 * 3. Al confirmar, ejecuta el callback `onSubmit` con la clave del componente y el título.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {React.ReactNode} [props.children] - Elementos o disparadores que renderizan el selector.
 * @param {boolean} props.isOpen - Indica si el drawer de selección está visible.
 * @param {Function} props.onClose - Callback para cerrar el drawer.
 * @param {Function} props.onSubmit - Callback ejecutado al finalizar la selección ({ selectedComponentKey, newComponentTitle }).
 *
 * @returns {React.ReactElement} Drawer lateral y diálogo de configuración de título.
 * 
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
export function ChartSelector({ children, isOpen, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [isTitleDialogOpen, setIsTitleDialogOpen] = React.useState(false);
  const [selectedComponentKey, setSelectedComponentKey] = React.useState(null);
  const [newComponentTitle, setNewComponentTitle] = React.useState('');

  /**
   * Maneja la selección inicial de un componente desde la lista.
   *
   * 1. Guarda la clave del componente seleccionado.
   * 2. Establece un título por defecto si está configurado en COMPONENTS_MAP.
   * 3. Cierra el drawer de selección y abre el diálogo de confirmación de título.
   *
   * @param {string} key - Identificador único del componente seleccionado.
   */
  function handleSelectComponent(key) {
    setSelectedComponentKey(key);
    const component = COMPONENTS_MAP[key];
    setNewComponentTitle(component.defaultTitleKey ? t(component.defaultTitleKey) : '');
    onClose();
    setIsTitleDialogOpen(true);
  }

  /**
   * Finaliza el proceso de selección y creación.
   * Ejecuta la función `onSubmit` propalando los datos seleccionados y cierra el diálogo.
   */
  function handleSubmitNewComponent() {
    onSubmit({ selectedComponentKey, newComponentTitle });
    setIsTitleDialogOpen(false);
  }

  return (
    <>
      {children}
      <Drawer anchor="right" open={isOpen} onClose={onClose}>
        <Box role="presentation" style={{ width: 300 }}>
          <Box padding={2}>
            <Typography variant="h4" style={{ marginBottom: 5 }}>
              {t('dynamicPanel.chartSelector.availableDashboards')}
            </Typography>
            <Typography variant="body2">
              {t('dynamicPanel.chartSelector.selectComponent')}
            </Typography>
          </Box>
          <Divider />
          <List>
            {Object.entries(COMPONENTS_MAP).map(([key, value]) => (
              <Tooltip key={key} title={value.descriptionKey ? t(value.descriptionKey) : ''}>
                <ListItem button onClick={() => handleSelectComponent(key)}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    width="100%"
                  >
                    <Typography style={{ marginBottom: 4, fontWeight: 'bold' }}>
                      {value.defaultTitleKey ? t(value.defaultTitleKey) : ''}
                    </Typography>
                    <Box>
                      <img
                        src={`/images/charts/${value.img}`}
                        alt={value.nameKey ? t(value.nameKey) : ''}
                        style={{ width: '100%' }}
                      />
                    </Box>
                  </Box>
                </ListItem>
              </Tooltip>
            ))}
          </List>
        </Box>
      </Drawer>

      <Dialog
        open={isTitleDialogOpen}
        onClose={() => setIsTitleDialogOpen(false)}
      >
        <DialogTitle>{t('dynamicPanel.chartSelector.enterChartTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('dynamicPanel.chartSelector.title')}
            fullWidth
            value={newComponentTitle}
            onChange={(e) => setNewComponentTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsTitleDialogOpen(false)} color="primary">
            {t('dynamicPanel.chartSelector.cancel')}
          </Button>
          <Button onClick={handleSubmitNewComponent} color="primary">
            {t('dynamicPanel.chartSelector.accept')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
